'use client';

/**
 * LegMiniMap: a strip of the leg's geography for phones, where the leg list
 * covers the map. The road as one line, the two towns at its ends, and a dot
 * per camp on screen (numbered like the rows), so "27 km from Lakes
 * Entrance" has a place to sit. Pure SVG: no tiles, nothing to load.
 */

import React, { useMemo } from 'react';
import type { LatLng } from '../../utils/campsAlong';

export interface MiniPin {
  id: number;
  lat: number;
  lng: number;
  n: number;
}

/** Fit points into a w×h box (equirectangular, lng scaled by cos(lat)). */
export function projectToBox(points: LatLng[], w: number, h: number, pad = 12): ((p: LatLng) => { x: number; y: number }) | null {
  if (!points.length) return null;
  const midLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const k = Math.cos((midLat * Math.PI) / 180);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of points) {
    const x = p.lng * k;
    const y = -p.lat;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const spanX = Math.max(maxX - minX, 1e-6);
  const spanY = Math.max(maxY - minY, 1e-6);
  const s = Math.min((w - pad * 2) / spanX, (h - pad * 2) / spanY);
  const ox = (w - spanX * s) / 2;
  const oy = (h - spanY * s) / 2;
  return (p: LatLng) => ({ x: ox + (p.lng * k - minX) * s, y: oy + (-p.lat - minY) * s });
}

export default function LegMiniMap({
  line,
  pins,
  fromLabel,
  toLabel,
  litId,
}: {
  line: LatLng[];
  pins: MiniPin[];
  fromLabel: string;
  toLabel: string;
  litId?: number | null;
}) {
  const W = 360;
  const H = 64;
  const geo = useMemo(() => {
    const step = Math.max(1, Math.floor(line.length / 160));
    const pts = line.filter((_, i) => i % step === 0 || i === line.length - 1);
    const proj = projectToBox([...pts, ...pins], W, H, 14);
    if (!proj || pts.length < 2) return null;
    const d = pts.map((p, i) => {
      const q = proj(p);
      return `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
    }).join('');
    return { d, a: proj(pts[0]), b: proj(pts[pts.length - 1]), pins: pins.map((p) => ({ ...p, ...proj(p) })) };
  }, [line, pins]);
  if (!geo) return null;
  return (
    <figure className="m-0 mx-4 mt-2.5 overflow-hidden rounded-xl bg-[#eef4f1]" data-testid="leg-mini-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-16 w-full" role="img" aria-label={`Map of the drive from ${fromLabel} to ${toLabel} with the camps on it`}>
        <path d={geo.d} fill="none" stroke="#ffffff" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        <path d={geo.d} fill="none" stroke="#0f766e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        {geo.pins.map((p) => {
          const lit = litId === p.id;
          return (
            <g key={p.id}>
              <circle cx={p.x} cy={p.y} r={lit ? 7 : 3.5} fill={lit ? '#1558b0' : '#ffffff'} stroke="#1558b0" strokeWidth={lit ? 2 : 1.5} />
              {lit && (
                <text x={p.x} y={p.y + 3} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#ffffff">
                  {p.n}
                </text>
              )}
            </g>
          );
        })}
        {[geo.a, geo.b].map((q, i) => (
          <circle key={i} cx={q.x} cy={q.y} r="5" fill="#16233a" stroke="#ffffff" strokeWidth="2" />
        ))}
      </svg>
    </figure>
  );
}
